import { EBuildMode } from "../environments";

const STAGING_PASSWORD = import.meta.env.VITE_STAGING_BASIC_AUTH_PASSWORD;
const IS_STAGING_BUILD = import.meta.env.MODE === EBuildMode.UAT;
const AUTH_SESSION_KEY = "narin:staging:auth";
const INTERACTION_OVERLAY_ID = "narin-interaction-overlay-layer";
const DEVTOOLS_OVERLAY_ID = "narin-devtools-overlay";

let hasInitializedInteractionGuard = false;
let hasInitializedDevToolsGuard = false;

const initializeInteractionGuard = () => {
  if (hasInitializedInteractionGuard || typeof document === "undefined") {
    return;
  }

  const attachListeners = () => {
    if (hasInitializedInteractionGuard || !document.body) {
      return;
    }

    let hideTimeout: ReturnType<typeof window.setTimeout> | null = null;
    const overlay =
      document.getElementById(INTERACTION_OVERLAY_ID) ??
      (() => {
        const element = document.createElement("div");
        element.id = INTERACTION_OVERLAY_ID;
        element.style.position = "fixed";
        element.style.inset = "0";
        element.style.backgroundColor = "#fff";
        element.style.opacity = "0";
        element.style.pointerEvents = "none";
        element.style.transition = "opacity 200ms ease";
        element.style.zIndex = "2147483647";
        document.body.appendChild(element);
        return element;
      })();

    const triggerWhiteout = () => {
      overlay.style.pointerEvents = "auto";
      overlay.style.opacity = "1";

      if (hideTimeout) {
        window.clearTimeout(hideTimeout);
      }

      hideTimeout = window.setTimeout(() => {
        overlay.style.opacity = "0";
        overlay.style.pointerEvents = "none";
        hideTimeout = null;
      }, 2000);
    };

    const blockContextMenu = (event: MouseEvent) => {
      event.preventDefault();
    };

    const blockDragStart = (event: DragEvent) => {
      if (event.target instanceof HTMLImageElement) {
        event.preventDefault();
      }
    };

    const blockKeyboardShortcuts = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();

      const isSaveShortcut =
        (event.ctrlKey || event.metaKey) && ["s", "p"].includes(key);
      const isPrintScreenKey = key === "printscreen";
      const isMacScreenshotShortcut =
        event.metaKey && event.shiftKey && ["3", "4", "5"].includes(key);
      const isSnippingShortcut =
        event.shiftKey && (event.ctrlKey || event.metaKey) && key === "s";

      if (isPrintScreenKey || isMacScreenshotShortcut || isSnippingShortcut) {
        event.preventDefault();
        triggerWhiteout();
        return;
      }

      if (isSaveShortcut) {
        event.preventDefault();
      }
    };

    document.addEventListener("contextmenu", blockContextMenu, {
      capture: true,
    });
    document.addEventListener("dragstart", blockDragStart, { capture: true });
    document.addEventListener("keydown", blockKeyboardShortcuts, {
      capture: true,
    });

    hasInitializedInteractionGuard = true;
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", attachListeners, {
      once: true,
    });
  } else {
    attachListeners();
  }
};

const initializeDevToolsGuard = () => {
  if (hasInitializedDevToolsGuard || typeof document === "undefined") {
    return;
  }

  const attachDevToolsDetection = () => {
    if (hasInitializedDevToolsGuard || !document.body) {
      return;
    }

    // Create persistent overlay
    const overlay = document.createElement("div");
    overlay.id = DEVTOOLS_OVERLAY_ID;
    overlay.style.cssText = `
      position: fixed;
      inset: 0;
      background-color: rgba(0, 0, 0, 0.95);
      display: none;
      align-items: center;
      justify-content: center;
      z-index: 2147483647;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;

    // Create message
    const message = document.createElement("div");
    message.style.cssText = `
      text-align: center;
      color: white;
      padding: 2rem;
    `;
    message.innerHTML = `
      <h1 style="font-size: 2rem; margin-bottom: 1rem;">⚠️ Developer Tools Detected</h1>
      <p style="font-size: 1.2rem; opacity: 0.9;">Please close the developer tools to continue.</p>
    `;
    overlay.appendChild(message);
    document.body.appendChild(overlay);

    let isDevToolsOpen = false;

    // Check if device is mobile
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

    // Multiple detection methods
    const detectDevTools = () => {
      // Skip detection on mobile devices as outerWidth/outerHeight
      // differs from innerWidth/innerHeight due to browser UI (address bar, navigation)
      if (isMobile) return;

      const threshold = 160;
      const widthThreshold = window.outerWidth - window.innerWidth > threshold;
      const heightThreshold =
        window.outerHeight - window.innerHeight > threshold;

      // Detect by console
      const devtoolsDetected = widthThreshold || heightThreshold;

      if (devtoolsDetected && !isDevToolsOpen) {
        isDevToolsOpen = true;
        overlay.style.display = "flex";
      } else if (!devtoolsDetected && isDevToolsOpen) {
        isDevToolsOpen = false;
        overlay.style.display = "none";
      }
    };

    // Additional detection using debugger timing
    const checkDebugger = () => {
      // Skip on mobile devices
      if (isMobile) return;

      const start = performance.now();
      // eslint-disable-next-line no-debugger
      debugger;
      const end = performance.now();

      if (end - start > 100) {
        if (!isDevToolsOpen) {
          isDevToolsOpen = true;
          overlay.style.display = "flex";
        }
      }
    };

    // Check on resize
    window.addEventListener("resize", detectDevTools);

    // Periodic checks
    setInterval(detectDevTools, 1000);
    setInterval(checkDebugger, 1000);

    // Initial check
    detectDevTools();

    hasInitializedDevToolsGuard = true;
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", attachDevToolsDetection, {
      once: true,
    });
  } else {
    attachDevToolsDetection();
  }
};

if (typeof window !== "undefined") {
  // Only enable interaction guard (right-click blocking, etc.) in production
  const isProduction = import.meta.env.MODE === EBuildMode.PRODUCTION;

  if (isProduction) {
    initializeInteractionGuard();
    // initializeDevToolsGuard();
  }

  // Enforce a simple blocking auth prompt before the rest of the app loads in staging builds.
  if (IS_STAGING_BUILD) {
    if (!STAGING_PASSWORD) {
      console.warn(
        "Staging auth guard is enabled but password is missing. Skipping guard."
      );
    } else {
      let session: Storage | null = null;
      try {
        session = window.sessionStorage;
      } catch (error) {
        console.warn(
          "Unable to access sessionStorage for staging auth guard.",
          error
        );
      }

      if (session) {
        const isVerified = session.getItem(AUTH_SESSION_KEY) === "true";

        if (!isVerified) {
          const MAX_ATTEMPTS = 3;
          let authenticated = false;

          for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
            const suppliedPassword = window.prompt("Staging access – password");
            if (suppliedPassword === null) {
              break;
            }

            if (suppliedPassword === STAGING_PASSWORD) {
              authenticated = true;
              break;
            }

            window.alert("Incorrect password. Please try again.");
          }

          if (authenticated) {
            session.setItem(AUTH_SESSION_KEY, "true");
          } else {
            session.removeItem(AUTH_SESSION_KEY);
            window.alert("Access denied.");
            window.location.replace("https://narinonline.com");
          }
        }
      }
    }
  }
}
